import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Brush,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts'


const monthTickFormatter = (tick) => {
    const date = new Date(tick)

    return date.getMonth() + 1
}

const renderYearTick = (tickProps) => {
    const { x, y, payload } = tickProps
    const { value } = payload
    const month = value.getMonth()
    const year = value.getFullYear()
    const showYearTick = month === 0

    return showYearTick ? (
        <text x={x} y={y} textAnchor="middle">{`${year}`}</text>
    ) : null
}

const MinMaxChart = ({ data }) => {
    const [isDesktop, setIsDesktop] = useState(window.matchMedia('(min-width: 768px)').matches)
    const processedData = data.map((item) => {
        return {
            ...item,
            minBalance: parseFloat(item.minBalance),
            maxBalance: parseFloat(item.maxBalance),
        }
    })

    const min = getMinNegativeRounded(processedData, ['minBalance', 'maxBalance'])
    const max = getMaxPositiveRounded(processedData, ['minBalance', 'maxBalance'])
    
    const responsiveAspect = isDesktop ? 2.25 : 1

    const listener = useDebouncedCallback(
        () => setIsDesktop(window.matchMedia('(min-width: 768px)').matches),
        300
    );

    useEffect(() => {
        window.addEventListener('resize', listener)
        window.addEventListener('DOMContentLoaded', listener)

        return () => {
            window.removeEventListener('resize', listener)
            window.removeEventListener('DOMContentLoaded', listener)
        }
    }, [])

    return (
        <ResponsiveContainer aspect={responsiveAspect}>
            <LineChart
                data={processedData}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    tickFormatter={monthTickFormatter}
                    strokeWidth={1}
                    xAxisId={0}
                />

                <XAxis
                    dataKey="date"
                    tick={renderYearTick}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    strokeWidth={1}
                    xAxisId={1}
                />
                <YAxis domain={[min, max]} type="number" />
                <Tooltip
                    labelFormatter={(value) => (
                        `${(value.getMonth() + 1).toString().padStart(2, '0')}/${value.getFullYear()}`
                    )}
                    formatter={(value, name) =>
                        [
                            parseFloat(value).toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            }),
                            name.substring(0, 3).charAt(0).toUpperCase() + name.substring(0, 3).slice(1)
                        ]
                    }
                 />
                <Legend
                    wrapperStyle={{
                        paddingTop: 15,
                        paddingBottom: 5,
                    }}
                    formatter={(value) => {
                        return value.substring(0, 3).charAt(0).toUpperCase() + value.substring(0, 3).slice(1)
                    }}
                />
                <Brush
                    dataKey="displayMonth"
                    height={30}
                    stroke="#8884d8"
                    travellerWidth={10}
                />
                <Line isAnimationActive={false} dataKey="maxBalance" activeDot={true} stroke="#000000" strokeWidth={3} />
                <Line isAnimationActive={false} dataKey="minBalance" activeDot={true} stroke="#999999" strokeWidth={3} />
                <ReferenceLine y={0} stroke="#000" isFront={true} />
            </LineChart>
        </ResponsiveContainer>
    )
}

const reduceByKey = (data, key, getMin = false) => {
    return data.reduce((prev, curr) => {
        if (getMin) {
            return prev[key] < curr[key] ? prev : curr
        }
        return prev[key] > curr[key] ? prev : curr
    })[key]
}

const getMaxPositiveRounded = (data, keys) => {
    const max = keys
        .map((key) => reduceByKey(data, key))
        .reduce((a, b) => Math.max(a, b))

    const rounded = Math.ceil(max / 1000) * 1000

    return rounded
}

const getMinNegativeRounded = (data, keys) => {
    const min = keys
        .map((key) => reduceByKey(data, key, true))
        .reduce((a, b) => Math.min(a, b))

    const rounded = Math.floor(min / 1000) * 1000

    return rounded
}

export default MinMaxChart
